import { ReactNode, useState } from 'react';

import { type Table } from '@tanstack/react-table';
import { DownloadIcon, FolderInputIcon, PlusIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SelectionActionButton,
  SelectionActions,
  TableToolbarRow,
} from '@/components/ui/table/table-toolbar';
import { Datasource } from '@/shared/types/collections';

import { MoveToFolder } from './MoveToFolder';
import { useTabCategory } from '../../hooks/useCategory';

// Static keys per category — avoids building translation keys via interpolation.
const SEARCH_PLACEHOLDER_KEYS: Record<string, string> = {
  faq: 'collections.search.faq',
  documents: 'collections.search.documents',
  webpages: 'collections.search.webpages',
  external: 'collections.search.external',
};

interface DocumentsTableToolbarProps<TData> {
  table: Table<TData>;
  search: string;
  onSearchChange: (value: string) => void;
  permissions: { canRead: boolean; canWrite: boolean; canDelete: boolean };
  /** Destination folders for the move-to-folder dropdown. */
  datasources: Datasource[] | undefined;
  onBulkDownload: (items: TData[]) => void;
  onMoveToFolder: (items: TData[], formData: { datasource_id: string }) => void;
  onBulkDelete: (items: TData[]) => void;
  /** Parent owns the side effect — the reset differs between the two tables. */
  onUpload: () => void;
  onCreate: () => void;
  hideCreate?: boolean;
  /** Table-specific selection actions (e.g. Create Folder), rendered before move-to-folder. */
  renderSelectionActions?: (items: TData[]) => ReactNode;
}

/**
 * Shared toolbar for the collections document tables (ContentTable, DatasourcesDocumentsTable). The
 * two tables wrap different data models but render an identical toolbar; the only divergence is the
 * datasources table's Create Folder dropdown, injected via `renderSelectionActions`.
 */
export function DocumentsTableToolbar<TData>({
  table,
  search,
  onSearchChange,
  permissions,
  datasources,
  onBulkDownload,
  onMoveToFolder,
  onBulkDelete,
  onUpload,
  onCreate,
  hideCreate = false,
  renderSelectionActions,
}: DocumentsTableToolbarProps<TData>) {
  const { t } = useTranslation();
  const [moveToFolderOpen, setMoveToFolderOpen] = useState(false);

  const { canRead, canWrite, canDelete } = permissions;
  const { isFAQTab, isDocumentsTab, isWebsiteTab, isExternalTab, category } = useTabCategory();

  // Full row objects for bulk actions. Search filtering happens upstream (useFilteredBySearch) before
  // the table sees the data, so filtered-out rows never enter the selection model in the first place.
  const selectedItems = table.getSelectedRowModel().rows.map((row) => row.original);

  const handleMoveToFolder = (formData: { datasource_id: string }) => {
    onMoveToFolder(selectedItems, formData);
    table.resetRowSelection();
    setMoveToFolderOpen(false);
  };

  return (
    <TableToolbarRow
      className="mb-5"
      start={
        <SearchBar
          placeholder={t(SEARCH_PLACEHOLDER_KEYS[category ?? ''] ?? 'collections.search.documents')}
          value={search}
          onChange={(event) => {
            onSearchChange(event.target.value);
            // Reset to the first page on search — a direct response to a user action, so it lives
            // here rather than in an effect. Done explicitly (not via `autoResetPageIndex`) so
            // document mutations (toggle/delete/move) don't bounce the user back to page 1.
            table.setPageIndex(0);
          }}
        />
      }
      center={
        <SelectionActions
          count={selectedItems.length}
          getLabel={(count) => t('collections.selected', { count })}
          onDeselectAll={() => table.resetRowSelection()}
        >
          {(isFAQTab || isDocumentsTab) && (
            <SelectionActionButton
              label={t('collections.download_selected_tooltip')}
              onClick={() => onBulkDownload(selectedItems)}
              disabled={!canRead}
            >
              <DownloadIcon />
            </SelectionActionButton>
          )}

          {renderSelectionActions?.(selectedItems)}

          <DropdownMenu open={moveToFolderOpen} onOpenChange={setMoveToFolderOpen}>
            <InfoTooltip content={t('collections.move_to_folder')}>
              <DropdownMenuTrigger asChild disabled={datasources?.length === 0}>
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  aria-label={t('collections.move_to_folder')}
                  disabled={!canWrite}
                >
                  <FolderInputIcon />
                </Button>
              </DropdownMenuTrigger>
            </InfoTooltip>
            <DropdownMenuContent className="w-xs" align="start">
              <MoveToFolder datasources={datasources} onSubmit={handleMoveToFolder} />
            </DropdownMenuContent>
          </DropdownMenu>

          <SelectionActionButton
            label={t('common.delete')}
            className="hover:text-destructive hover:[&_svg]:text-destructive"
            onClick={() => onBulkDelete(selectedItems)}
            disabled={!canDelete}
          >
            <Trash2Icon />
          </SelectionActionButton>
        </SelectionActions>
      }
      end={
        <>
          {(isFAQTab || isDocumentsTab) && (
            <Button variant="outline-neutral" size="sm" onClick={onUpload} disabled={!canWrite}>
              <UploadIcon />
              {t('common.upload')}
            </Button>
          )}

          {!hideCreate && (
            <Button onClick={onCreate} disabled={!canWrite} variant="outline-neutral" size="sm">
              <PlusIcon />
              {isWebsiteTab || isExternalTab ? t('common.add') : t('common.create')}
            </Button>
          )}
        </>
      }
    />
  );
}
